import React, { useState } from "react";
import { Mail, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Change login email: new address (+ current password) → 6-digit OTP sent to the NEW address → verified → new JWT.
export default function ChangeEmailDialog() {
  const { user, applySession } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => { setStep(1); setNewEmail(""); setPassword(""); setOtp(""); setErr(""); };

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/change-email/request", { new_email: newEmail.trim(), password: user.has_password ? password : null });
      toast.success(data.message);
      setStep(2);
    } catch (ex) { setErr(formatApiErrorDetail(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/change-email/verify", { otp: otp.trim() });
      applySession(data.token, data.user);
      toast.success(data.message);
      setOpen(false); reset();
    } catch (ex) { setErr(formatApiErrorDetail(ex.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => { reset(); setOpen(true); }} className="rounded-md h-9 shrink-0" data-testid="change-email-btn">
        <Mail size={14} className="mr-1.5" /> Change email
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <DialogContent data-testid="change-email-dialog">
          <DialogHeader>
            <DialogTitle>{step === 1 ? "Change login email" : "Enter the 6-digit code"}</DialogTitle>
            <DialogDescription>
              {step === 1
                ? `Your current login email is ${user.email}. We'll send a verification code to the new address before switching.`
                : `We emailed a code to ${newEmail}. It expires in 15 minutes.`}
            </DialogDescription>
          </DialogHeader>
          {step === 1 ? (
            <form onSubmit={requestOtp} className="space-y-3">
              <div>
                <Label>New email address</Label>
                <Input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="you@example.com" autoFocus data-testid="change-email-new" />
              </div>
              {user.has_password && (
                <div>
                  <Label>Current password (to confirm it&rsquo;s you)</Label>
                  <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="change-email-password" />
                </div>
              )}
              {err && <div className="text-sm text-red-600" data-testid="change-email-error">{err}</div>}
              <Button type="submit" disabled={busy || !newEmail} className="w-full bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="change-email-send">
                {busy ? "Sending code…" : "Send verification code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-3">
              <div>
                <Label>Verification code</Label>
                <Input inputMode="numeric" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" className="text-center text-2xl tracking-[0.5em] font-mono" autoFocus data-testid="change-email-otp" />
              </div>
              {err && <div className="text-sm text-red-600" data-testid="change-email-error">{err}</div>}
              <Button type="submit" disabled={busy || otp.length !== 6} className="w-full bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md" data-testid="change-email-verify">
                <ShieldCheck size={14} className="mr-1.5" /> {busy ? "Verifying…" : "Confirm new email"}
              </Button>
              <button type="button" onClick={() => { setStep(1); setOtp(""); setErr(""); }} className="text-xs text-[#5C6B7A] inline-flex items-center gap-1 hover:text-[#C85A32]" data-testid="change-email-back"><ArrowLeft size={12} /> Use a different address / resend</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
